import { COMPASS_STEPS } from '@/components/flows/CompassFlow/constants'
import { buildUrl } from '@/lib/helpers/helperFunctions'
import { PARAMS } from '@/lib/params'
import { SEARCH_PARAMS } from '@/lib/searchParams'
import { StringMap } from '@/types/misc'

/*
 * ⚠️ When making changes to routes, do the following:
 *
 * 1) Check the below repos to ensure you update any old route references:
 * - RQ-API
 * - RQ-keycloak
 * - RQ-keycloak-spi
 *
 * 2) Add an entry in redirectService.ts to ensure the old URLs are redirected
 * to the new URL.
 */

export const ROUTES = {
  /**
   * ---------------------------------------------------------------------------
   * Guest
   * ---------------------------------------------------------------------------
   */
  _index: '/',
  index: () => ROUTES._index,

  _signIn: '/sign-in',
  signIn: (context?: RouteContext<undefined, typeof SEARCH_PARAMS.signIn>) =>
    buildUrl(ROUTES._signIn, context),

  _signOut: '/sign-out',
  signOut: (context: RouteContext<undefined, typeof SEARCH_PARAMS.signOut>) =>
    buildUrl(ROUTES._signOut, context),

  _signUp: '/sign-up',
  signUp: (context: RouteContext<undefined, typeof SEARCH_PARAMS.signUp>) =>
    buildUrl(ROUTES._signUp, context),

  /**
   * ---------------------------------------------------------------------------
   * Company portal
   * ---------------------------------------------------------------------------
   */
  _addCompany: '/add-company',
  addCompany: () => ROUTES._addCompany,

  _addExistingAdviser: '/add-existing-adviser',
  addExistingAdviser: () => ROUTES._addExistingAdviser,

  _addRelationship: '/professional-network/add-relationship',
  addRelationship: () => ROUTES._addRelationship,

  _addRelationshipAgreement:
    '/professional-network/relationships/:panelId/add-agreement',
  addRelationshipAgreement: (
    context: RouteContext<
      typeof PARAMS.addRelationshipAgreement,
      typeof SEARCH_PARAMS.addRelationshipAgreement
    >,
  ) => buildUrl(ROUTES._addRelationshipAgreement, context),

  _calendarSettingsPage: '/settings/calendar',
  calendarSettingsPage: () => ROUTES._calendarSettingsPage,

  _challenge: '/challenge',
  challenge: (
    context: RouteContext<undefined, typeof SEARCH_PARAMS.challenge>,
  ) => buildUrl(ROUTES._challenge, context),

  _clients: '/clients',
  clients: (context?: RouteContext<undefined, typeof SEARCH_PARAMS.clients>) =>
    buildUrl(ROUTES._clients, context),

  _clientsImport: '/clients/import',
  clientsImport: () => ROUTES._clientsImport,

  _commercialAgreementEdit:
    '/commercial-agreements/:commercialAgreementId/edit',
  commercialAgreementEdit: (
    context: RouteContext<
      typeof PARAMS.commercialAgreementEdit,
      typeof SEARCH_PARAMS.commercialAgreementEdit
    >,
  ) => buildUrl(ROUTES._commercialAgreementEdit, context),

  _commercialAgreementTemplateReview: `/commercial-agreement-templates/:${PARAMS.commercialAgreementTemplateReview.commercialAgreementTemplateId}/review`,
  commercialAgreementTemplateReview: (
    context: RouteContext<
      typeof PARAMS.commercialAgreementTemplateReview,
      typeof SEARCH_PARAMS.commercialAgreementTemplateReview
    >,
  ) => buildUrl(ROUTES._commercialAgreementTemplateReview, context),

  // ⚠️ Update references to this route on RQ-APi if you update this
  _commercialAgreementTemplateRevisions:
    '/commercial-agreement-template-revisions',
  commercialAgreementTemplateRevisions: () =>
    ROUTES._commercialAgreementTemplateRevisions,

  _companyCompassAdmin: '/company-compass/admin',
  companyCompassAdmin: () => ROUTES._companyCompassAdmin,

  _companyCompassDashboard: '/company-compass/dashboard',
  companyCompassDashboard: () => ROUTES._companyCompassDashboard,

  _companyCompassSnapshots: '/company-compass/snapshots',
  companyCompassSnapshots: (
    context?: RouteContext<
      undefined,
      typeof SEARCH_PARAMS.companyCompassSnapshots
    >,
  ) => buildUrl(ROUTES._companyCompassSnapshots, context),

  _companyDashboard: '/dashboard',
  companyDashboard: () => ROUTES._companyDashboard,

  _compassPdfReportPreview: '/compass/pdf-report/preview',
  compassPdfReportPreview: (
    context?: RouteContext<
      undefined,
      typeof SEARCH_PARAMS.compassPdfReportPreview
    >,
  ) => buildUrl(ROUTES._compassPdfReportPreview, context),

  _compassPdfReportSection: '/compass/pdf-report/:section',
  compassPdfReportSection: (
    context: RouteContext<
      typeof PARAMS.compassPdfReportSection,
      typeof SEARCH_PARAMS.compassPdfReportSection
    >,
  ) => buildUrl(ROUTES._compassPdfReportSection, context),

  _companyProfile: '/company-profile',
  companyProfile: (
    context: RouteContext<undefined, typeof SEARCH_PARAMS.companyProfile>,
  ) => buildUrl(ROUTES._companyProfile, context),

  _completeRegistrationAtSignup: '/signup/complete-registration', // This route is only used on initial sign-up
  completeRegistrationAtSignup: () => ROUTES._completeRegistrationAtSignup,

  _completeRegistrationAtSignupStep: '/signup/complete-registration/:step', // This route is only used on initial sign-up
  completeRegistrationAtSignupStep: () =>
    ROUTES._completeRegistrationAtSignupStep,

  _completeRegistration: '/complete-registration', // This route can be used at any time after sign up
  completeRegistration: () => ROUTES._completeRegistration,

  _completeRegistrationStep: '/complete-registration/:step', // This route can be used at any time after sign up
  completeRegistrationStep: () => ROUTES._completeRegistrationStep,

  _componentReference: '/component-reference',
  componentReference: () => ROUTES._componentReference,

  _demoVideos: '/demo-videos',
  demoVideos: () => ROUTES._demoVideos,

  _debug: '/debug',
  debug: () => ROUTES._debug,

  _disclaimer: '/disclaimer',
  disclaimer: () => ROUTES._disclaimer,

  _domainAccessDenied: '/domain-access-denied',
  domainAccessDenied: () => ROUTES._domainAccessDenied,

  _contactUs: '/contact-us/:companyReferralCode',
  contactUs: (context: RouteContext<typeof PARAMS.contactUs>) =>
    buildUrl(ROUTES._contactUs, context),

  _directLeadCaptureEmbed: '/direct-lead-capture/embed/:companyReferralCode',
  directLeadCaptureEmbed: (
    context: RouteContext<typeof PARAMS.directLeadCaptureEmbed>,
  ) => buildUrl(ROUTES._directLeadCaptureEmbed, context),

  _emailAndCalendarIntegrationReconnect:
    '/integrations/email-and-calendar/reconnect/:nylasInvalidGrantId', // Update `NylasRedirectEnum` in RQ-API if changing this
  emailAndCalendarIntegrationReconnect: (
    context: RouteContext<typeof PARAMS.emailAndCalendarReconnect>,
  ) => buildUrl(ROUTES._emailAndCalendarIntegrationReconnect, context),

  _emailAndCalendarIntegrationReconnected:
    '/integrations/email-and-calendar/reconnected', // Update `NylasRedirectEnum` in RQ-API if changing this
  emailAndCalendarIntegrationReconnected: () =>
    ROUTES._emailAndCalendarIntegrationReconnected,

  _findProfessional: '/find-professional',
  findProfessional: () => ROUTES._findProfessional,

  _groupPreferences: '/group/preferences',
  groupPreferences: () => ROUTES._groupPreferences,

  _hubSpotIntegration: '/integrations/hubspot',
  hubSpotIntegration: () => ROUTES._hubSpotIntegration,

  _hubSpotIntegrationRegister: '/integrations/hubspot/register',
  hubSpotIntegrationRegister: () => ROUTES._hubSpotIntegrationRegister,

  _mobileLanding: '/mobile',
  mobileLanding: () => ROUTES._mobileLanding,

  _help: '/help',
  help: (context?: RouteContext<undefined, typeof SEARCH_PARAMS.help>) =>
    buildUrl(ROUTES._help, context),

  _intellifloIntegration: '/integrations/intelliflo',
  intellifloIntegration: () => ROUTES._intellifloIntegration,

  _intellifloIntegrationQueue: '/integrations/intelliflo/queue',
  intellifloIntegrationQueue: () => ROUTES._intellifloIntegrationQueue,

  _intellifloIntegrationRedirect: '/integrations/intelliflo/redirect/:tenantId',
  intellifloIntegrationRedirect: () => ROUTES._intellifloIntegrationRedirect,

  _intellifloIntegrationAuthCallback: '/integrations/auth/INTELLIFLO',
  intellifloIntegrationAuthCallback: () =>
    ROUTES._intellifloIntegrationAuthCallback,

  _manageLocations: '/manage-locations',
  manageLocations: () => ROUTES._manageLocations,

  _addLocation: '/manage-locations/add',
  addLocation: () => ROUTES._addLocation,

  _editLocation: `/locations/:${PARAMS.editLocation.locationId}/edit`,
  editLocation: (context: RouteContext<typeof PARAMS.editLocation>) =>
    buildUrl(ROUTES._editLocation, context),

  _manageReferralStatuses: '/manage-referral-statuses',
  manageReferralStatuses: () => ROUTES._manageReferralStatuses,

  _manageUsers: '/manage-users',
  manageUsers: (
    context?: RouteContext<undefined, typeof SEARCH_PARAMS.manageUsers>,
  ) => buildUrl(ROUTES._manageUsers, context),

  _manageUserProvisioning: '/manage-users/auto-provisioning',
  manageUserProvisioning: () => ROUTES._manageUserProvisioning,

  _plannrIntegration: '/integrations/plannr',
  plannrIntegration: () => ROUTES._plannrIntegration,

  _plannrIntegrationRegister: '/integrations/plannr/register',
  plannrIntegrationRegister: () => ROUTES._plannrIntegrationRegister,

  _preferences: '/preferences',
  preferences: () => ROUTES._preferences,

  _professionalNetworkApprovalManagement:
    '/professional-network/approval-management',
  professionalNetworkApprovalManagement: () =>
    ROUTES._professionalNetworkApprovalManagement,

  _professionalNetworkInvite: '/professional-network/invite',
  professionalNetworkInvite: () => ROUTES._professionalNetworkInvite,

  _professionalNetworkOnboarding: '/professional-network/onboarding',
  professionalNetworkOnboarding: () => ROUTES._professionalNetworkOnboarding,

  _professionalNetworkRelationships: '/professional-network/relationships',
  professionalNetworkRelationships: (
    context?: RouteContext<
      undefined,
      typeof SEARCH_PARAMS.professionalNetworkRelationships
    >,
  ) => buildUrl(ROUTES._professionalNetworkRelationships, context),

  _professionalNetworkApprovalRequests:
    '/professional-network/approval-requests',
  professionalNetworkApprovalRequests: () =>
    ROUTES._professionalNetworkApprovalRequests,

  _professionalNetworkRevenue: '/professional-network/revenue',
  professionalNetworkRevenue: () => ROUTES._professionalNetworkRevenue,

  _professionalNetworkStats: '/professional-network/stats',
  professionalNetworkStats: () => ROUTES._professionalNetworkStats,

  _referralAffiliateLink: '/referrals/:clientCode/guest/visit-supplier',
  referralAffiliateLink: (
    context: RouteContext<typeof PARAMS.referralAffiliateLink>,
  ) => buildUrl(ROUTES._referralAffiliateLink, context),

  _recommendation: '/recommendation/:referralCode',
  recommendation: (context: RouteContext<typeof PARAMS.recommendation>) =>
    buildUrl(ROUTES._recommendation, context),

  _recommendations: '/recommendations',
  recommendations: () => ROUTES._recommendations,

  // Update INDEXABLE_ROUTE_PATTERNS from responseHeadersService.ts if you
  // update the URL here
  _recommendationGenerateLink:
    '/recommendation/:companyReferralCode/generate-link',

  recommendationGenerateLink: (
    context: RouteContext<typeof PARAMS.recommendationGenerateLink>,
  ) => buildUrl(ROUTES._recommendationGenerateLink, context),

  _recommendationsSettings: '/client-referrals/settings',
  recommendationsSettings: () => ROUTES._recommendationsSettings,

  _referralPagesPage: '/settings/referral-pages',
  referralPagesPage: () => ROUTES._referralPagesPage,

  _referralRoutingPage: '/settings/referral-routing',
  referralRoutingPage: () => ROUTES._referralRoutingPage,

  _referClient: '/refer-client',
  referClient: (
    context?: RouteContext<undefined, typeof SEARCH_PARAMS.referClient>,
  ) => buildUrl(ROUTES._referClient, context),

  _referrals: '/referrals',
  referrals: (
    context?: RouteContext<undefined, typeof SEARCH_PARAMS.referrals>,
  ) => buildUrl(ROUTES._referrals, context),

  _referralDiscovery: '/referral-discovery',
  referralDiscovery: () => ROUTES._referralDiscovery,

  _referralESignatureConsent:
    '/referrals/:referralId/e-signature-consent/:callbackCode',
  referralESignatureConsent: (
    context: RouteContext<typeof PARAMS.referralESignatureConsent>,
  ) => buildUrl(ROUTES._referralESignatureConsent, context),

  _referralESignatureConsentPdf:
    '/referrals/:referralId/e-signature-consent/pdf/:callbackCode',
  referralESignatureConsentPdf: (
    context: RouteContext<
      typeof PARAMS.referralESignatureConsentPdf,
      typeof SEARCH_PARAMS.referralESignatureConsentPdf
    >,
  ) => buildUrl(ROUTES._referralESignatureConsentPdf, context),

  _referralESignatureConsentPdfPreview:
    '/referrals/:referralId/e-signature-consent/pdf-preview',
  referralESignatureConsentPdfPreview: (
    context: RouteContext<
      typeof PARAMS.referralESignatureConsentPdfPreview,
      typeof SEARCH_PARAMS.referralESignatureConsentPdfPreview
    >,
  ) => buildUrl(ROUTES._referralESignatureConsentPdfPreview, context),

  _referralFeedback: '/referrals/:referralId/feedback/:token',
  referralFeedback: (context: RouteContext<typeof PARAMS.referralFeedback>) =>
    buildUrl(ROUTES._referralFeedback, context),

  _referralInvites: '/referrals/invites/:token', // Update User::extractReferralInviteData if you update this
  referralInvites: (context: RouteContext<typeof PARAMS.referralInvites>) =>
    buildUrl(ROUTES._referralInvites, context),

  _referralInvite: '/referrals/invites/:token/:referralId', // Update User::extractReferralInviteData if you update this
  referralInvite: (context: RouteContext<typeof PARAMS.referralInvite>) =>
    buildUrl(ROUTES._referralInvite, context),

  // Dedicated page that builds the referrals CSV export in its own tab so the
  // heavy fetch/render/scrape doesn't freeze the main app. Filters and the
  // active tab are passed through the query string.
  _referralsCsvExport: '/referrals/csv-export',
  referralsCsvExport: () => ROUTES._referralsCsvExport,

  _referralLinkOld: '/referral-links/:referralLinkCode', // Only used for older URLs
  _referralLink: '/referral-links/:referralLinkId/:referralLinkCode',
  referralLink: (context: RouteContext<typeof PARAMS.referralLink>) =>
    buildUrl(ROUTES._referralLink, context),

  _referralRetrospectiveESignatureConsent:
    '/referrals/:referralId/retrospective-e-signature-consent/:callbackCode',
  referralRetrospectiveESignatureConsent: (
    context: RouteContext<typeof PARAMS.referralRetrospectiveESignatureConsent>,
  ) => buildUrl(ROUTES._referralRetrospectiveESignatureConsent, context),

  _referralSummary: '/referrals/:referralId',
  referralSummary: (
    context: RouteContext<
      typeof PARAMS.referralSummary,
      typeof SEARCH_PARAMS.referralSummary
    >,
  ) => buildUrl(ROUTES._referralSummary, context),

  _rqCertifiedApplicationProcess: '/rq-certified/application-process',
  rqCertifiedApplicationProcess: () => ROUTES._rqCertifiedApplicationProcess,

  _rqCertifiedApplicationStatus: '/rq-certified/application-status',
  rqCertifiedApplicationStatus: () => ROUTES._rqCertifiedApplicationStatus,

  _rqCertifiedGlossary: '/rq-certified/glossary',
  rqCertifiedGlossary: () => ROUTES._rqCertifiedGlossary,

  _rqCertifiedNdaPdf: '/rq-certified/:companyId/nda',
  rqCertifiedNdaPdf: (context: RouteContext<typeof PARAMS.rqCertifiedNdaPdf>) =>
    buildUrl(ROUTES._rqCertifiedNdaPdf, context),

  _rqCertifiedPdfGuides: '/rq-certified/pdf-guides',
  rqCertifiedPdfGuides: () => ROUTES._rqCertifiedPdfGuides,

  _shareRecommendation: `/recommendation/:${PARAMS.shareRecommendation.referralCode}/share`,
  shareRecommendation: (
    context: RouteContext<typeof PARAMS.shareRecommendation>,
  ) => buildUrl(ROUTES._shareRecommendation, context),

  _walkthrough: '/walkthrough',
  walkthrough: () => ROUTES._walkthrough,

  _xeroIntegration: '/integrations/xero',
  xeroIntegration: () => ROUTES._xeroIntegration,

  _xeroIntegrationRegister: '/integrations/xero/register',
  xeroIntegrationRegister: () => ROUTES._xeroIntegrationRegister,

  /**
   * ---------------------------------------------------------------------------
   * Compass portal
   * ---------------------------------------------------------------------------
   */
  _compassActions: '/compass/snapshots/:token/actions',
  compassActions: (context: RouteContext<typeof PARAMS.compassActions>) =>
    buildUrl(ROUTES._compassActions, context),

  _compassConfirmSharing: '/compass/:token/confirm-sharing',
  compassConfirmSharing: (
    context: RouteContext<typeof PARAMS.compassConfirmSharing>,
  ) => buildUrl(ROUTES._compassConfirmSharing, context),

  _compassDashboard: '/compass/dashboard',
  compassDashboard: () => ROUTES._compassDashboard,

  _compassDisclaimer: '/compass/disclaimer',
  compassDisclaimer: () => ROUTES._compassDisclaimer,

  _compassDetailsAlreadyOnFile:
    '/compass/:companyReferralCode/details-already-on-file',
  compassDetailsAlreadyOnFile: (
    context: RouteContext<typeof PARAMS.compassDetailsAlreadyOnFile>,
  ) => buildUrl(ROUTES._compassDetailsAlreadyOnFile, context),

  _compassHowItWorks: '/compass/how-it-works',
  compassHowItWorks: () => ROUTES._compassHowItWorks,

  _compassSharing: '/compass/sharing',
  compassSharing: () => ROUTES._compassSharing,

  _compassSnapshots: '/compass/snapshots',
  compassSnapshots: () => ROUTES._compassSnapshots,

  _compassSnapshot: '/compass/snapshots/:token',
  compassSnapshot: (
    context: RouteContext<
      typeof PARAMS.compassSnapshot,
      typeof SEARCH_PARAMS.compassSnapshot
    >,
  ) => {
    const { params, searchParams } = context || {}
    const step = searchParams?.step ?? COMPASS_STEPS.clientDetails

    return buildUrl(ROUTES._compassSnapshot, {
      params,
      searchParams: {
        ...searchParams,
        step,
      },
    })
  },

  _compassStart: '/compass/start',
  compassStart: () => ROUTES._compassStart,

  _compassUpdate: '/compass/update',
  compassUpdate: () => ROUTES._compassUpdate,

  _compassPartners: '/compass/partners',
  compassPartners: () => ROUTES._compassPartners,

  /**
   * ---------------------------------------------------------------------------
   * Group portal
   * ---------------------------------------------------------------------------
   */
  _groupDashboard: '/group/dashboard',
  groupDashboard: () => ROUTES._groupDashboard,

  _groupOverview: '/group/overview',
  groupOverview: () => ROUTES._groupOverview,

  /**
   * ---------------------------------------------------------------------------
   * Network portal
   * ---------------------------------------------------------------------------
   */
  _networkOverview: '/network/overview',
  networkOverview: () => ROUTES._networkOverview,
  _networkDashboard: '/network/dashboard',
  networkDashboard: () => ROUTES._networkDashboard,
  _networkReferralRegister: '/network/referrals',
  networkReferralRegister: () => ROUTES._networkReferralRegister,

  /**
   * ---------------------------------------------------------------------------
   * Guest landing - black template
   * ---------------------------------------------------------------------------
   */
  _autoProvisionDomainVerification:
    '/company-user-provisioning/:id/verify-domain/:token',
  autoProvisionDomainVerification: (
    context: RouteContext<typeof PARAMS.autoProvisionDomainVerification>,
  ) => buildUrl(ROUTES._autoProvisionDomainVerification, context),

  _clientPayment: '/client-payment/:id/:code',
  clientPayment: (context: RouteContext<typeof PARAMS.clientPayment>) =>
    buildUrl(ROUTES._clientPayment, context),

  _commissionConsent: '/referral/commission-consent/:id/:code',
  commissionConsent: (context: RouteContext<typeof PARAMS.commissionConsent>) =>
    buildUrl(ROUTES._commissionConsent, context),

  _confirmConsent: '/referral/confirm-consent/:id/:code',
  confirmConsent: (context: RouteContext<typeof PARAMS.confirmConsent>) =>
    buildUrl(ROUTES._confirmConsent, context),

  _inviteUser: '/invite-user/:id/:code',
  inviteUser: (context: RouteContext<typeof PARAMS.inviteUser>) =>
    buildUrl(ROUTES._inviteUser, context),

  _inviteGroupUser: '/invite-group-user/:id/:code',
  inviteGroupUser: (context: RouteContext<typeof PARAMS.inviteGroupUser>) =>
    buildUrl(ROUTES._inviteGroupUser, context),

  _requestCallback: '/referral/request-callback/:id/:code',
  requestCallback: (
    context: RouteContext<
      typeof PARAMS.requestCallback,
      typeof SEARCH_PARAMS.requestCallback
    >,
  ) => buildUrl(ROUTES._requestCallback, context),

  _rescheduleMeeting: '/referral/reschedule-meeting/:id/:code',
  rescheduleMeeting: (context: RouteContext<typeof PARAMS.rescheduleMeeting>) =>
    buildUrl(ROUTES._rescheduleMeeting, context),

  _withdrawGdprConsent: '/referral/withdraw-gdpr-consent/:id/:code',
  withdrawGdprConsent: (
    context: RouteContext<typeof PARAMS.withdrawGdprConsent>,
  ) => buildUrl(ROUTES._withdrawGdprConsent, context),

  _verifyPublicEmail: '/verify-public-email',
  verifyPublicEmail: () => ROUTES._verifyPublicEmail,

  /**
   * ---------------------------------------------------------------------------
   * Guest landing - white template
   * ---------------------------------------------------------------------------
   */
  _companyInvitation: '/invitation/:referralCode/:companyTypeCode?', // :companyTypeCode isn't used but is kept to support old URLs
  companyInvitation: (context: RouteContext<typeof PARAMS.companyInvitation>) =>
    buildUrl(ROUTES._companyInvitation, context),

  /**
   * ---------------------------------------------------------------------------
   * Authenticated landing
   * ---------------------------------------------------------------------------
   */
  _companySuspended: '/company-suspended',
  companySuspended: () => ROUTES._companySuspended,

  _companyRegistration: '/register',
  companyRegistration: () => ROUTES._companyRegistration,

  _shortlist: '/shortlist',
  shortlist: () => ROUTES._shortlist,

  _welcome: '/welcome',
  welcome: () => ROUTES._welcome,

  _invitations: '/invitations',
  invitations: () => ROUTES._invitations,

  /**
   * ---------------------------------------------------------------------------
   * Misc
   * ---------------------------------------------------------------------------
   */
  _companyCompass: '/compass',
  companyCompass: (
    context?: RouteContext<undefined, typeof SEARCH_PARAMS.companyCompass>,
  ) => buildUrl(ROUTES._companyCompass, context),

  _companyCompassGlobalLink: '/company-compass/global-link',
  companyCompassGlobalLink: () => ROUTES._companyCompassGlobalLink,

  _companyCompassInvite: '/company-compass/:companyReferralCode',
  companyCompassInvite: (
    context: RouteContext<
      typeof PARAMS.companyCompassInvite,
      typeof SEARCH_PARAMS.companyCompassInvite
    >,
  ) => buildUrl(ROUTES._companyCompassInvite, context),

  _companyCompassInvitePreview: '/company-compass/:companyReferralCode/preview',
  companyCompassInvitePreview: (
    context: RouteContext<typeof PARAMS.companyCompassInvitePreview>,
  ) => buildUrl(ROUTES._companyCompassInvitePreview, context),

  _companyCompassView: '/company-compass/view',
  companyCompassView: (
    context: RouteContext<undefined, typeof SEARCH_PARAMS.companyCompassView>,
  ) => buildUrl(ROUTES._companyCompassView, context),

  _compassConfirmStart: '/compass/:token/confirm-start',
  compassConfirmStart: (
    context: RouteContext<typeof PARAMS.compassConfirmStart>,
  ) => buildUrl(ROUTES._compassConfirmStart, context),

  _compassConfirmSharingViaGlobalLink:
    '/compass/:companyReferralCode/confirm-sharing-via-global-link',
  compassConfirmSharingViaGlobalLink: (
    context: RouteContext<typeof PARAMS.compassConfirmSharingViaGlobalLink>,
  ) => buildUrl(ROUTES._compassConfirmSharingViaGlobalLink, context),

  _compassPdfGuide: '/compass/:companyId/pdf-guide',
  compassPdfGuide: (context: RouteContext) =>
    buildUrl(ROUTES._compassPdfGuide, context),

  _compassRedirect: '/compass/redirect',
  compassRedirect: (
    context?: RouteContext<undefined, typeof SEARCH_PARAMS.compassRedirect>,
  ) => buildUrl(ROUTES._compassRedirect, context),

  _notFound: '/not-found',
  notFound: () => ROUTES._notFound,

  _invalidOrExpired: '/invalid-or-expired',
  invalidOrExpired: () => ROUTES._invalidOrExpired,

  _redirect: '/redirect',
  redirect: (context: RouteContext<undefined, typeof SEARCH_PARAMS.redirect>) =>
    buildUrl(ROUTES._redirect, context),

  _redirectNewUser: '/redirect/new-user',
  redirectNewUser: () => ROUTES._redirectNewUser,

  splashScreen: import.meta.env.VITE_SPLASH_URL,

  _userCompass: '/user-compass', // For clients accessing Compass via invite link
  userCompass: (
    context?: RouteContext<undefined, typeof SEARCH_PARAMS.userCompass>,
  ) => buildUrl(ROUTES._userCompass, context),

  _xplanIntegration: '/integrations/xplan',
  xplanIntegration: () => ROUTES._xplanIntegration,

  _xplanIntegrationRedirect: '/integrations/xplan/redirect',
  xplanIntegrationRedirect: () => ROUTES._xplanIntegrationRedirect,

  // Embed iframe routes
  _referralIframe: '/iframe/referrals/:referralId',
  referralIframe: (context: RouteContext<typeof PARAMS.referralIframe>) =>
    buildUrl(ROUTES._referralIframe, context),
  accountSettingsIframe: '/iframe/account-settings',
  mappingIframe: '/iframe/mapping',
  introducersIframe: '/iframe/introducers',
  _clientReferralsCompanyOverviewIframe:
    '/iframe/client-referrals-company-overview',
  _clientReferralsCompanyOverviewReferralsIframe:
    '/iframe/client-referrals-company-overview/referrals',
  _referIframe: '/iframe/refer',
  _referSuccessIframe: '/iframe/refer-success',
  _clientReferralsIframe: '/iframe/client-referrals',

  /**
   * ---------------------------------------------------------------------------
   * Office task pane
   * ---------------------------------------------------------------------------
   */
  _officeCheckAuth: '/office365app/auth-check',
  officeCheckAuth: () => ROUTES._officeCheckAuth,

  _officeConnectEmailAndCalendar: '/office365app/connect-email-and-calendar',
  officeConnectEmailAndCalendar: () => ROUTES._officeConnectEmailAndCalendar,

  _officeTaskPane: '/office365app/taskpane', // Landing page for Office 365 addons
  officeTaskPane: () => ROUTES._officeTaskPane,

  _officeReferralConfirmation: '/office365app/referrals/:referralId',
  officeReferralConfirmation: (
    context: RouteContext<typeof PARAMS.officeReferralConfirmation>,
  ) => buildUrl(ROUTES._officeReferralConfirmation, context),

  _officeInaccessibleReferralConfirmation:
    '/office365app/referrals/inaccessible-referral-confirmation',
  officeInaccessibleReferralConfirmation: () =>
    ROUTES._officeInaccessibleReferralConfirmation,

  /**
   * ---------------------------------------------------------------------------
   * ICAEW
   * ---------------------------------------------------------------------------
   */
  _icaewOverview: '/icaew/overview',
  icaewOverview: () => ROUTES._icaewOverview,

  _icaewFirmSearch: '/icaew/firm-search',
  icaewFirmSearch: () => ROUTES._icaewFirmSearch,

  _icaewTrends: '/icaew/trends',
  icaewTrends: () => ROUTES._icaewTrends,

  _icaewDisclaimer: '/icaew/disclaimer',
  icaewDisclaimer: () => ROUTES._icaewDisclaimer,
}

export type RouteKey = keyof typeof ROUTES
export type RoutePath = (typeof ROUTES)[RouteKey]

type RouteContext<
  TParams extends StringMap | undefined = undefined,
  TSearchParams extends StringMap | undefined = undefined,
> = {
  params?: TParams extends StringMap ? TParams : undefined
  searchParams?: TSearchParams extends StringMap
    ? Partial<TSearchParams>
    : undefined
}

export const PDF_ROUTES = [
  ROUTES._compassPdfReportSection,
  ROUTES._compassPdfGuide,
  ROUTES._referralESignatureConsentPdf,
  ROUTES._rqCertifiedPdfGuides,
  ROUTES._rqCertifiedNdaPdf,
]
